/**
 * LoginScreenBase for Poker Reborn
 *
 * Abstract base class for Web and Native login screen implementations.
 * Contains shared UI (background, title, status/error text) and lifecycle.
 *
 * @see docs/screens/login/README.md - Visual specs, auth flow, error handling
 */

import { Container, Graphics, Text } from 'pixi.js';
import { log } from '@/utils/log';
import { authService } from '@/services/AuthService';
import { AuthError } from '@/types/auth';

// =============================================================================
// LOGIN SCREEN BASE
// =============================================================================

export abstract class LoginScreenBase extends Container {
  protected background: Graphics;
  protected title: Text;
  protected statusText: Text;
  protected errorText: Text;

  protected screenWidth: number;
  protected screenHeight: number;

  /** Callback when credentials are received (set by main.ts) */
  public onCredentialsReceived?: (username: string, password: string) => void;

  constructor(screenWidth: number, screenHeight: number) {
    super();

    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    // Create background
    this.background = new Graphics();
    this.addChild(this.background);

    // Create title
    this.title = new Text({
      text: 'Poker Reborn',
      style: {
        fill: 0xd4af37, // Gold
        fontSize: 48,
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: 'bold',
        dropShadow: {
          color: 0x000000,
          blur: 8,
          distance: 4,
        },
      },
    });
    this.title.anchor.set(0.5);
    this.addChild(this.title);

    // Create status text (for loading states)
    this.statusText = new Text({
      text: '',
      style: {
        fill: 0xcccccc,
        fontSize: 18,
        fontFamily: 'Roboto, Arial, sans-serif',
      },
    });
    this.statusText.anchor.set(0.5);
    this.addChild(this.statusText);

    // Create error text
    this.errorText = new Text({
      text: '',
      style: {
        fill: 0xff6b6b, // Soft red
        fontSize: 16,
        fontFamily: 'Roboto, Arial, sans-serif',
        align: 'center',
      },
    });
    this.errorText.anchor.set(0.5);
    this.addChild(this.errorText);

    // Initial layout for shared elements
    this.layoutBase();

    // Subclass mounts its buttons
    this.mountButtons();

    // Re-layout after buttons are mounted
    this.layoutBase();
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /** Resize handler */
  resize(screenWidth: number, screenHeight: number): void {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
    this.layoutBase();
    this.onResize();
  }

  /** Clean up resources (DOM elements, PixiJS buttons) */
  cleanup(): void {
    this.cleanupImpl();
  }

  /** Destroy and cleanup */
  destroy(): void {
    this.cleanup();
    super.destroy();
  }

  // ===========================================================================
  // PROTECTED — Shared UI helpers
  // ===========================================================================

  protected setStatus(message: string): void {
    this.statusText.text = message;
  }

  protected showError(message: string): void {
    this.errorText.text = message;
  }

  protected hideError(): void {
    this.errorText.text = '';
  }

  /** Handle guest login — shared across both platforms (native only) */
  protected async handleGuestLogin(): Promise<void> {
    log.auth.info`Guest login initiated`;
    this.setStatus('Joining as guest...');
    this.hideError();

    try {
      const credentials = await authService.guestLogin();

      // Cleanup and notify
      this.cleanup();
      this.onCredentialsReceived?.(credentials.username, credentials.password);
    } catch (error) {
      this.handleAuthError(error);
    }
  }

  /** Handle auth errors with user-friendly messages */
  protected handleAuthError(error: unknown): void {
    this.setStatus('');

    if (error instanceof AuthError) {
      switch (error.code) {
        case 'invalid_token':
          this.showError('Google authentication expired. Please try again.');
          break;
        case 'account_disabled':
          this.showError('Your account has been disabled. Contact support.');
          break;
        case 'credentials_invalid':
          this.showError('Your session has expired. Please log in again.');
          break;
        case 'network_error':
          this.showError('Connection failed. Check your internet and try again.');
          break;
        case 'unauthorized':
          this.showError('Configuration error. Contact support.');
          break;
        default:
          this.showError('Sign-in failed. Please try again.');
      }
    } else if (error instanceof TypeError && error.message.includes('fetch')) {
      this.showError('Cannot connect to server. Please try again later.');
    } else {
      this.showError('Something went wrong. Please try again.');
    }

    const errorDetail = error instanceof Error ? error.message : JSON.stringify(error);
    log.app.error`Auth error: ${errorDetail}`;
  }

  // ===========================================================================
  // PROTECTED — Layout
  // ===========================================================================

  /** Position shared elements (background, title, status/error text) */
  protected layoutBase(): void {
    const { screenWidth: w, screenHeight: h } = this;
    const centerX = w / 2;
    const centerY = h / 2;

    // Draw background
    this.background.clear();
    this.background.rect(0, 0, w, h);
    this.background.fill(0x1a1a2e);

    // Position title (upper third)
    this.title.x = centerX;
    this.title.y = h / 3;

    // Position status text (below button area)
    this.statusText.x = centerX;
    this.statusText.y = centerY + 160;

    // Position error text (below status)
    this.errorText.x = centerX;
    this.errorText.y = centerY + 190;
  }

  // ===========================================================================
  // ABSTRACT — Subclass hooks
  // ===========================================================================

  /** Mount platform-specific buttons. Called once from constructor. */
  protected abstract mountButtons(): void;

  /** Clean up platform-specific resources (DOM elements or PixiJS buttons). */
  protected abstract cleanupImpl(): void;

  /** Called on resize — subclasses reposition their buttons. */
  protected abstract onResize(): void;
}
